import { AdminFetchUsBankEarningOrders, AdminFetchUsBankEarnings } from 'src/constants/actionTypes'
import type {
  FetchUsBankEarningOrdersPayload,
  FetchUsBankEarningsPayload,
  UsBankEarningOrder,
  UsBankEarningsPage,
} from 'src/interfaces/usBankTransactions'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { UsBankTransactionsState } from './types'

/** Text filters are trimmed to match what the saga sends; `''` and `null` are the same filter. */
const normalizeText = (value: string | null): string | null => {
  return value?.trim() || null
}

/** The filters only — a page change keeps the same key, so the grid does not blank. */
const buildKey = (payload?: FetchUsBankEarningsPayload): string => {
  if (!payload) {
    return ''
  }

  return JSON.stringify({
    contractorId: normalizeText(payload.contractorId),
    contractorExternalId: normalizeText(payload.contractorExternalId),
    partnerDisplayName: normalizeText(payload.partnerDisplayName),
    destinationType: payload.destinationType,
    orderStatus: payload.orderStatus,
    uploadedFrom: payload.uploadedFrom,
    uploadedTo: payload.uploadedTo,
    activeOnly: payload.activeOnly,
  })
}

const getInitialState = (): UsBankTransactionsState => {
  return {
    earnings: [],
    pagination: {},
    loadedKey: null,
    requestedKey: null,
    loading: false,
    error: null,
    orders: [],
    ordersEarningId: null,
    ordersLoading: false,
    ordersError: null,
  }
}

export const usBankTransactionsReducer = (
  state: UsBankTransactionsState = getInitialState(),
  action: Action<unknown>,
): UsBankTransactionsState => {
  switch (action.type) {
    case AdminFetchUsBankEarnings.type.REQUEST: {
      const requestedKey = buildKey(action.payload as FetchUsBankEarningsPayload | undefined)
      const isSameKey = requestedKey === state.loadedKey

      return {
        ...state,
        earnings: isSameKey ? state.earnings : [],
        pagination: isSameKey ? state.pagination : {},
        loadedKey: isSameKey ? state.loadedKey : null,
        requestedKey,
        loading: true,
        error: null,
      }
    }
    case AdminFetchUsBankEarnings.type.SUCCESS: {
      const page = action.payload as UsBankEarningsPage | undefined

      return {
        ...state,
        earnings: page?.data ?? [],
        pagination: page?.pagination ?? {},
        loadedKey: state.requestedKey,
        loading: false,
        error: null,
      }
    }
    case AdminFetchUsBankEarnings.type.FAILED:
      return {
        ...state,
        loading: false,
        error: resolveErrorMessage(action.payload, 'Failed to load US Bank transactions'),
      }

    case AdminFetchUsBankEarningOrders.type.REQUEST: {
      const earningId =
        (action.payload as FetchUsBankEarningOrdersPayload | undefined)?.earningId ?? null
      const isSameEarning = earningId === state.ordersEarningId

      return {
        ...state,
        orders: isSameEarning ? state.orders : [],
        ordersEarningId: earningId,
        ordersLoading: true,
        ordersError: null,
      }
    }
    case AdminFetchUsBankEarningOrders.type.SUCCESS:
      return {
        ...state,
        orders: (action.payload as UsBankEarningOrder[] | undefined) ?? [],
        ordersLoading: false,
        ordersError: null,
      }
    case AdminFetchUsBankEarningOrders.type.FAILED:
      return {
        ...state,
        ordersLoading: false,
        ordersError: resolveErrorMessage(action.payload, 'Failed to load orders'),
      }

    default:
      return state
  }
}
